"use client";

import React from "react";
import { motion } from "framer-motion";
import { 
  Headset, 
  Mail, 
  Phone, 
  MessageSquare, 
  Clock, 
  MapPin,
  ArrowRight
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const SupportPage = () => {
  return (
    <div className="bg-white min-h-screen font-jakarta">
      {/* Hero Section */}
      <section className="bg-[#063258] py-20 px-6 text-center text-white">
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="max-w-4xl mx-auto"
        >
          <div className="flex justify-center mb-6">
            <div className="bg-[#FFAE00] p-4 rounded-full">
              <Headset className="w-10 h-10 text-[#063258]" />
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-bricolage font-bold mb-6">
            We&apos;re Here to Help
          </h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Have questions about a trip? Need assistance with your booking? 
            Our support team is ready to assist you on your journey.
          </p>
        </motion.div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* WhatsApp Support */}
          <motion.div 
            whileHover={{ y: -10 }}
            className="bg-gray-50 p-8 rounded-3xl border border-gray-100 shadow-sm text-center"
          >
            <div className="bg-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bricolage font-bold mb-4">WhatsApp Us</h3>
            <p className="text-gray-600 mb-6">
              Instant support for quick queries and real-time updates.
            </p>
            <Link href="https://wa.me/917982579691" target="_blank">
              <Button className="w-full bg-green-500 hover:bg-green-600 text-white rounded-xl py-6 text-lg font-bold">
                Chat Now
              </Button>
            </Link>
          </motion.div>

          {/* Email Support */}
          <motion.div 
            whileHover={{ y: -10 }}
            className="bg-gray-50 p-8 rounded-3xl border border-gray-100 shadow-sm text-center"
          >
            <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bricolage font-bold mb-4">Email Us</h3>
            <p className="text-gray-600 mb-6">
              For detailed inquiries, partnerships, or documentation help.
            </p>
            <Link href="mailto:support@outcomeouting.com">
              <Button className="w-full bg-[#063258] hover:bg-[#0D0D0D] text-white rounded-xl py-6 text-lg font-bold">
                Send Email
              </Button>
            </Link>
          </motion.div>

          {/* Call Support */}
          <motion.div 
            whileHover={{ y: -10 }}
            className="bg-gray-50 p-8 rounded-3xl border border-gray-100 shadow-sm text-center"
          >
            <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Phone className="w-8 h-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-bricolage font-bold mb-4">Call Us</h3>
            <p className="text-gray-600 mb-6">
              Speak directly with our travel experts for personalized help.
            </p>
            <Link href="tel:+917982579691">
              <Button className="w-full bg-[#FFAE00] hover:bg-orange-500 text-white rounded-xl py-6 text-lg font-bold">
                Call +91 79825 79691
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bricolage font-bold mb-8">Other Ways to Connect</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="mt-1 bg-white p-2 rounded-lg shadow-sm">
                    <Clock className="w-5 h-5 text-[#FFAE00]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Support Hours</h4>
                    <p className="text-gray-600">Monday - Saturday: 10:00 AM - 8:00 PM IST</p>
                    <p className="text-gray-600 font-medium">Emergency Support: 24/7 during active trips</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="mt-1 bg-white p-2 rounded-lg shadow-sm">
                    <MapPin className="w-5 h-5 text-[#FFAE00]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Registered Office</h4>
                    <p className="text-gray-600">B-116 Sector 6 Noida, Uttar Pradesh, 201301, India</p>

                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-[#FFAE00] rounded-3xl rotate-3 -z-10 opacity-20"></div>
              <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
                <h3 className="text-2xl font-bricolage font-bold mb-4">Quick Response Promise</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  We value your time. Our team typically responds to WhatsApp and Call inquiries within 30 minutes during business hours. Email inquiries are addressed within 24 hours.
                </p>
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <p className="text-[#063258] font-medium flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                    Currently Online & Ready to Help
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 text-center">
        <h2 className="text-3xl font-bricolage font-bold mb-8">Ready to start your journey?</h2>
        <Link href="/trails">
          <Button className="bg-[#FFAE00] hover:bg-orange-500 text-white px-10 py-7 rounded-2xl text-xl font-bold flex items-center gap-3 mx-auto transition-all shadow-lg hover:shadow-orange-200">
            Explore Experiences <ArrowRight />
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default SupportPage;
